import { memo, useState, useCallback } from "react";
import { 
  Radar, RefreshCw, Search, Zap, BarChart3, TrendingDown, TrendingUp, 
  Target, History, Send, Bot, Users, X, Loader2, ChevronDown, ChevronUp
} from "lucide-react";
import { formatTickerPrice, formatNumber, getTickerCurrency } from "../utils/trading";
import TopConvictionsCompact from "./TopConvictionsCompact";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectorIcon = ({ sector }) => {
  const icons = { "Tech": Radar, "AI/GPU": Radar, "Defense": Radar, "Energy": Radar, "Crypto": Radar };
  return <Radar className="w-4 h-4" />;
};

const SignalBadge = ({ signal }) => {
  const styles = {
    "STRONG_BUY": "bg-cyan-500/25 text-cyan-300 border-cyan-500/40",
    "BUY": "bg-cyan-500/15 text-cyan-400 border-cyan-500/25",
    "HOLD": "bg-blue-500/20 text-blue-400 border-blue-500/30",
    "SELL": "bg-red-500/20 text-red-400 border-red-500/30",
    "STRONG_SELL": "bg-red-500/30 text-red-300 border-red-500/50",
  };
  return (
    <span className={`px-3 py-1 rounded text-xs font-bold uppercase border ${styles[signal] || "bg-slate-700 text-slate-300 border-slate-600"}`}>
      {signal?.replace("_", " ")}
    </span>
  );
};

function Sparkline({ values, width = 80, height = 28 }) {
  if (!values || values.length < 2) return <span className="text-slate-500 text-xs">—</span>;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((v, i) => `${(i / (values.length - 1)) * width},${height - ((v - min) / range) * height}`).join(" ");
  const isUp = values[values.length - 1] >= values[0];
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline points={points} fill="none" stroke={isUp ? "#22c55e" : "#ef4444"} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
    </svg>
  );
}

// Inline AI analysis result panel
const ScanAnalysisPanel = ({ result, loading, onClose }) => {
  const [expanded, setExpanded] = useState(true);

  if (!loading && !result) return null;

  return (
    <div className="glass-card rounded-xl border border-cyan-500/20 bg-cyan-500/[0.02] overflow-hidden mb-4 animate-fade-in" data-testid="scan-analysis-panel">
      <div className="px-5 py-3 border-b border-cyan-500/15 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-bold text-cyan-400">Analyse IA du Scan</span>
          {loading && <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setExpanded(e => !e)} className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors" data-testid="scan-analysis-toggle">
            {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
          </button>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800/50 transition-colors" data-testid="scan-analysis-close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="px-5 py-4 max-h-[500px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-3 py-6 justify-center">
              <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              <span className="text-sm text-slate-400">Les experts analysent {">"}300 actifs...</span>
            </div>
          ) : result?.error ? (
            <div className="text-sm text-red-400">{result.error}</div>
          ) : (
            <div className="prose prose-invert prose-sm max-w-none text-slate-300 leading-relaxed whitespace-pre-wrap text-sm" data-testid="scan-analysis-content">
              {result?.response || "Pas de résultat."}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(function ScannerTab({
  scanMode, setScanMode,
  massiveScan, onTriggerMassiveScan,
  massiveFilter, setMassiveFilter,
  massiveSignalFilter, setMassiveSignalFilter,
  alertHistory,
  scanning, runMarketScan,
  scanResults, scanProgress,
  selectedSectors, setSelectedSectors,
  SECTOR_OPTIONS,
  scanChatInput, setScanChatInput,
  onAnalyze, onAskAI, onSwitchToAnalyst, aiLoading,
  algoTopRated,
}) {
  const [scanAnalysis, setScanAnalysis] = useState(null);
  const [scanAnalysisLoading, setScanAnalysisLoading] = useState(false);

  const runScanAnalysis = useCallback(async (prompt, scanData) => {
    setScanAnalysisLoading(true);
    setScanAnalysis(null);
    try {
      const res = await fetch(`${API}/ai/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: prompt,
          scan_results: scanData,
          session_id: `scan-analysis-${Date.now()}`
        })
      });
      const data = await res.json();
      setScanAnalysis(data);
    } catch (e) {
      setScanAnalysis({ error: "Erreur de connexion. Réessayez." });
    }
    setScanAnalysisLoading(false);
  }, []);

  const buildScanContext = useCallback(() => {
    const allResults = massiveScan.results.sort((a, b) => b.score - a.score);
    const topResults = allResults.filter(r => r.score >= 30).slice(0, 15).map(r =>
      `${r.ticker}: $${r.price} | Score:${r.score} | ${r.signal} | RSI:${r.rsi} | 1J:${r.change_1d >= 0 ? "+" : ""}${r.change_1d}% | 1S:${r.change_1w >= 0 ? "+" : ""}${r.change_1w}% | 1M:${r.change_1m >= 0 ? "+" : ""}${r.change_1m}% | Vol:${r.vol_ratio}x | Sect:${r.sector}`
    ).join("\n");
    const oversold = allResults.filter(r => r.rsi <= 30).slice(0, 5).map(r => `${r.ticker}: RSI ${r.rsi}, $${r.price}, ${r.signal}`).join(", ");
    const volSpikes = allResults.filter(r => r.vol_ratio >= 2.5).slice(0, 5).map(r => `${r.ticker}: ${r.vol_ratio}x vol, ${r.signal}`).join(", ");
    return `DONNÉES SCAN (${massiveScan.results.length} actifs):\n\nTOP 15 PAR SCORE:\n${topResults}\n\nOVERSOLD (RSI<30): ${oversold || "Aucun"}\nVOLUME SPIKES (>2.5x): ${volSpikes || "Aucun"}`;
  }, [massiveScan.results]);

  return (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-3"><Radar className="w-7 h-7 text-emerald-400" />Market Scanner</h1>
          <p className="text-slate-400 mt-1 text-sm">{scanMode === "massive" ? `${massiveScan.results.length || "300+"}  actifs scannés automatiquement` : "Sector-based opportunity detection"}</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-slate-800 rounded-lg p-0.5">
            <button onClick={() => setScanMode("massive")} className={`px-4 py-2 rounded-md text-xs font-bold transition-all ${scanMode === "massive" ? "bg-cyan-500/20 text-cyan-400" : "text-slate-400"}`} data-testid="scan-mode-massive">
              <Zap className="w-3.5 h-3.5 inline mr-1" />MASSIVE ({massiveScan.results.length || "300+"})
            </button>
            <button onClick={() => setScanMode("sector")} className={`px-4 py-2 rounded-md text-xs font-bold transition-all ${scanMode === "sector" ? "bg-cyan-500/20 text-cyan-400" : "text-slate-400"}`} data-testid="scan-mode-sector">
              <Search className="w-3.5 h-3.5 inline mr-1" />SECTEUR
            </button>
          </div>
          {scanMode === "massive" ? (
            <button onClick={onTriggerMassiveScan} disabled={massiveScan.loading}
              className={`px-6 py-2 rounded-xl font-bold text-xs transition-all flex items-center gap-2 ${massiveScan.loading ? "bg-slate-700 text-slate-400" : "bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 shadow-glow"}`}
              data-testid="massive-scan-btn">
              {massiveScan.loading ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Scanning...</> : <><RefreshCw className="w-3.5 h-3.5" />Re-scanner</>}
            </button>
          ) : (
            <button onClick={runMarketScan} disabled={scanning}
              className={`px-6 py-2 rounded-xl font-bold text-xs transition-all flex items-center gap-2 ${scanning ? "bg-slate-700 text-slate-400" : "bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 shadow-glow"}`}
              data-testid="scan-btn">
              {scanning ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Scanning {scanProgress.current}/{scanProgress.total}</> : <><Search className="w-3.5 h-3.5" />Run Scan</>}
            </button>
          )}
        </div>
      </div>

      {/* TOP 5 CONVICTIONS */}
      {algoTopRated && algoTopRated.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <TopConvictionsCompact
            topRated={algoTopRated}
            onAnalyze={onAnalyze}
            title="Top 5 Scanner"
          />
        </div>
      )}

      {/* MASSIVE SCAN MODE */}
      {scanMode === "massive" && (
        <>
          {/* OUTLIER ALERTS */}
          {massiveScan.results.length > 0 && (() => {
            const volSpikes = massiveScan.results.filter(r => r.vol_ratio >= 3).sort((a, b) => b.vol_ratio - a.vol_ratio).slice(0, 5);
            const bigDrops = massiveScan.results.filter(r => r.change_1w <= -8).sort((a, b) => a.change_1w - b.change_1w).slice(0, 5);
            const bigSpikes = massiveScan.results.filter(r => r.change_1w >= 8).sort((a, b) => b.change_1w - a.change_1w).slice(0, 5);
            const oversold = massiveScan.results.filter(r => r.rsi <= 28).sort((a, b) => a.rsi - b.rsi).slice(0, 5);
            const totalAlerts = volSpikes.length + bigDrops.length + bigSpikes.length + oversold.length;
            if (totalAlerts === 0) return null;
            return (
              <div className="glass-card rounded-xl p-4 mb-4 border border-amber-500/20 bg-amber-500/[0.03]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center"><Zap className="w-3.5 h-3.5 text-amber-400" /></div>
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">Alertes — {totalAlerts} mouvements anormaux</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {volSpikes.length > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="text-[9px] font-bold text-amber-400 uppercase mb-2 flex items-center gap-1"><BarChart3 className="w-3 h-3" />Volume Explosif</div>
                      {volSpikes.map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-1 cursor-pointer hover:bg-slate-800/50 rounded px-1 -mx-1" onClick={() => onAnalyze(r.ticker)}>
                          <span className="text-[10px] font-bold text-white">{r.ticker}</span>
                          <span className="text-[10px] font-mono font-bold text-amber-400">{r.vol_ratio}x vol</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {bigDrops.length > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="text-[9px] font-bold text-red-400 uppercase mb-2 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Chutes Majeures</div>
                      {bigDrops.map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-1 cursor-pointer hover:bg-slate-800/50 rounded px-1 -mx-1" onClick={() => onAnalyze(r.ticker)}>
                          <span className="text-[10px] font-bold text-white">{r.ticker}</span>
                          <span className="text-[10px] font-mono font-bold text-red-400">{r.change_1w.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {bigSpikes.length > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="text-[9px] font-bold text-emerald-400 uppercase mb-2 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Explosions Haussières</div>
                      {bigSpikes.map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-1 cursor-pointer hover:bg-slate-800/50 rounded px-1 -mx-1" onClick={() => onAnalyze(r.ticker)}>
                          <span className="text-[10px] font-bold text-white">{r.ticker}</span>
                          <span className="text-[10px] font-mono font-bold text-emerald-400">+{r.change_1w.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {oversold.length > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-3">
                      <div className="text-[9px] font-bold text-cyan-400 uppercase mb-2 flex items-center gap-1"><Target className="w-3 h-3" />RSI Survente</div>
                      {oversold.map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-1 cursor-pointer hover:bg-slate-800/50 rounded px-1 -mx-1" onClick={() => onAnalyze(r.ticker)}>
                          <span className="text-[10px] font-bold text-white">{r.ticker}</span>
                          <span className="text-[10px] font-mono font-bold text-cyan-400">RSI {r.rsi}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Filters */}
          <div className="glass-card rounded-xl p-4 mb-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <div className="text-[9px] text-slate-500 uppercase mb-1">Secteur</div>
                <div className="flex flex-wrap gap-1">
                  {["ALL","US_MEGA","US_GROWTH","US_SMALL","AI_SEMIS","DEFENSE","ENERGY","CRYPTO","CRYPTO_COINS","HEALTHCARE","FINTECH","EU_LARGE","ASIA","ETFS"].map(s => (
                    <button key={s} onClick={() => setMassiveFilter(s)}
                      className={`px-2.5 py-1 rounded text-[9px] font-bold transition-all ${massiveFilter === s ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40" : "bg-slate-800 text-slate-400 border border-slate-700"}`}>
                      {s === "ALL" ? "Tous" : s.replace("_"," ")}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[9px] text-slate-500 uppercase mb-1">Signal</div>
                <div className="flex flex-wrap gap-1">
                  {["ALL","OVERSOLD","OVERBOUGHT","VOLUME SPIKE","BULLISH","BEARISH"].map(s => (
                    <button key={s} onClick={() => setMassiveSignalFilter(s)}
                      className={`px-2.5 py-1 rounded text-[9px] font-bold transition-all ${massiveSignalFilter === s
                        ? s === "OVERSOLD" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                        : s === "OVERBOUGHT" ? "bg-red-500/20 text-red-400 border border-red-500/40"
                        : s === "VOLUME SPIKE" ? "bg-amber-500/20 text-amber-400 border border-amber-500/40"
                        : s === "BULLISH" ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                        : s === "BEARISH" ? "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                        : "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                        : "bg-slate-800 text-slate-400 border border-slate-700"}`}>
                      {s === "ALL" ? "Tous" : s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sector Heatmap */}
          {massiveScan.results.length > 0 && (() => {
            const sectorMap = {};
            massiveScan.results.forEach(r => {
              if (!sectorMap[r.sector]) sectorMap[r.sector] = { tickers: [], sum1d: 0, sum1w: 0, sum1m: 0, count: 0 };
              const s = sectorMap[r.sector]; s.tickers.push(r); s.sum1d += r.change_1d; s.sum1w += r.change_1w; s.sum1m += r.change_1m; s.count++;
            });
            const sectors = Object.entries(sectorMap).map(([name, s]) => ({
              name, avg1d: s.count ? (s.sum1d / s.count) : 0, avg1w: s.count ? (s.sum1w / s.count) : 0, avg1m: s.count ? (s.sum1m / s.count) : 0, count: s.count,
              top: s.tickers.sort((a, b) => b.change_1w - a.change_1w)[0], worst: s.tickers.sort((a, b) => a.change_1w - b.change_1w)[0],
            })).sort((a, b) => b.avg1w - a.avg1w);
            const maxAbs = Math.max(...sectors.map(s => Math.abs(s.avg1w)), 1);
            return (
              <div className="glass-card rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Heatmap Secteurs — Performance 1 semaine</div>
                  <div className="flex items-center gap-3 text-[9px] text-slate-500">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-500/40" />Hausse</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/40" />Baisse</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {sectors.map(s => {
                    const intensity = Math.min(Math.abs(s.avg1w) / maxAbs, 1);
                    const isUp = s.avg1w >= 0;
                    const bg = isUp ? `rgba(16,185,129,${0.08 + intensity * 0.25})` : `rgba(239,68,68,${0.08 + intensity * 0.25})`;
                    const border = isUp ? `rgba(16,185,129,${0.15 + intensity * 0.3})` : `rgba(239,68,68,${0.15 + intensity * 0.3})`;
                    return (
                      <button key={s.name} onClick={() => { setMassiveFilter(s.name); setMassiveSignalFilter("ALL"); }}
                        className="rounded-lg p-3 text-left transition-all hover:scale-[1.02] cursor-pointer"
                        style={{ backgroundColor: bg, borderWidth: 1, borderColor: border }} data-testid={`heatmap-${s.name}`}>
                        <div className="flex justify-between items-start">
                          <div className="text-[10px] font-bold text-white truncate">{s.name.replace("_"," ")}</div>
                          <div className={`font-mono text-xs font-bold ${isUp ? "text-emerald-400" : "text-red-400"}`}>{isUp ? "+" : ""}{s.avg1w.toFixed(1)}%</div>
                        </div>
                        <div className="text-[8px] text-slate-500 mt-0.5">{s.count} actifs</div>
                        {s.top && (
                          <div className="flex justify-between mt-1.5">
                            <span className="text-[8px] text-emerald-400 font-mono">{s.top.ticker} +{s.top.change_1w.toFixed(0)}%</span>
                            <span className="text-[8px] text-red-400 font-mono">{s.worst.ticker} {s.worst.change_1w.toFixed(0)}%</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Alert Track Record */}
          {alertHistory.length > 0 && (
            <div className="glass-card rounded-xl p-3 sm:p-4 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <History className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider">Track Record des Alertes</span>
                  <span className="text-[9px] text-slate-500 bg-slate-800 px-2 py-0.5 rounded">{alertHistory.length} alertes</span>
                </div>
                {(() => {
                  const withPnl = alertHistory.filter(a => a.pnl_since_alert != null && a.pnl_since_alert !== 0);
                  const winners = withPnl.filter(a => a.pnl_since_alert > 0).length;
                  const total = withPnl.length;
                  const winRate = total > 0 ? ((winners / total) * 100).toFixed(0) : "--";
                  const avgPnl = total > 0 ? (withPnl.reduce((s, a) => s + a.pnl_since_alert, 0) / total).toFixed(1) : "--";
                  return (
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-slate-400">Win Rate: <span className={`font-bold ${Number(winRate) >= 50 ? "text-emerald-400" : "text-red-400"}`}>{winRate}%</span></span>
                      <span className="text-[10px] text-slate-400">Avg P&L: <span className={`font-mono font-bold ${Number(avgPnl) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{avgPnl}%</span></span>
                    </div>
                  );
                })()}
              </div>
              <div className="max-h-48 overflow-y-auto space-y-0.5">
                {alertHistory.map((a, i) => {
                  const typeConfig = {
                    "VOLUME_SPIKE": { label: "VOL SPIKE", color: "text-amber-400 bg-amber-500/10" },
                    "BIG_DROP": { label: "CHUTE", color: "text-red-400 bg-red-500/10" },
                    "BIG_SPIKE": { label: "HAUSSE", color: "text-emerald-400 bg-emerald-500/10" },
                    "RSI_OVERSOLD": { label: "RSI SURV.", color: "text-cyan-400 bg-cyan-500/10" },
                  };
                  const cfg = typeConfig[a.alert_type] || { label: a.alert_type, color: "text-slate-400 bg-slate-700" };
                  const pnl = a.pnl_since_alert || 0;
                  const daysAgo = Math.floor((Date.now() - new Date(a.date).getTime()) / 86400000);
                  return (
                    <div key={i} className="py-1.5 px-2 rounded hover:bg-slate-800/50 cursor-pointer border-b border-slate-800/30" onClick={() => onAnalyze(a.ticker)}>
                      <div className="hidden sm:flex items-center gap-2">
                        <span className="text-[9px] text-slate-500 w-16 font-mono">{a.date?.slice(5)}</span>
                        <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${cfg.color} w-16 text-center`}>{cfg.label}</span>
                        <span className="text-[10px] font-bold text-white w-16">{a.ticker}</span>
                        <span className="text-[9px] font-mono text-slate-400 w-14">${a.alert_price}</span>
                        <span className="text-[9px] text-slate-600">→</span>
                        <span className="text-[9px] font-mono text-slate-300 w-14">${a.current_price || "..."}</span>
                        <span className={`text-[10px] font-mono font-bold w-16 text-right ${pnl > 0 ? "text-emerald-400" : pnl < 0 ? "text-red-400" : "text-slate-500"}`}>{pnl > 0 ? "+" : ""}{pnl.toFixed(1)}%</span>
                        <span className="text-[8px] text-slate-600">{daysAgo === 0 ? "auj." : `${daysAgo}j`}</span>
                      </div>
                      <div className="sm:hidden flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded shrink-0 ${cfg.color}`}>{cfg.label}</span>
                          <span className="text-[10px] font-bold text-white">{a.ticker}</span>
                          <span className="text-[9px] text-slate-500 font-mono">{a.date?.slice(5)}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[9px] font-mono text-slate-400">${a.alert_price}→${a.current_price || "..."}</span>
                          <span className={`text-[10px] font-mono font-bold ${pnl > 0 ? "text-emerald-400" : pnl < 0 ? "text-red-400" : "text-slate-500"}`}>{pnl > 0 ? "+" : ""}{pnl.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Massive Results Table */}
          {(() => {
            const filtered = massiveScan.results.filter(r => {
              if (massiveFilter !== "ALL" && r.sector !== massiveFilter) return false;
              if (massiveSignalFilter !== "ALL" && r.signal !== massiveSignalFilter) return false;
              return true;
            });
            return filtered.length > 0 ? (
              <div className="glass-card rounded-xl overflow-hidden mb-4">
                <div className="px-4 py-3 border-b border-slate-700 flex justify-between items-center">
                  <h2 className="font-display text-sm font-bold text-white">{filtered.length} actifs trouvés <span className="text-slate-500 font-normal">sur {massiveScan.results.length}</span></h2>
                  {massiveScan.scannedAt && <span className="text-[9px] text-slate-500">Scan: {new Date(massiveScan.scannedAt).toLocaleTimeString("fr-FR")}</span>}
                </div>
                <div className="hidden sm:grid px-4 py-2 bg-slate-800/50 border-b border-slate-700 text-[10px] text-slate-400 font-bold uppercase" style={{ gridTemplateColumns: '2fr 0.8fr 0.5fr 0.8fr 0.7fr 0.7fr 0.7fr 0.6fr 0.7fr 0.6fr' }}>
                  <div>Asset</div><div className="text-right">Prix</div><div className="text-center">Score</div>
                  <div className="text-center">Signal</div><div className="text-right">1J</div><div className="text-right">1S</div>
                  <div className="text-right">1M</div><div className="text-right">RSI</div><div className="text-right">Vol Ratio</div><div className="text-center">Secteur</div>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {filtered.map((r, i) => (
                    <div key={i} data-testid={`massive-row-${r.ticker}`}>
                      <div className="hidden sm:grid px-4 py-2.5 items-center row-hover border-b border-slate-700/30 cursor-pointer"
                        style={{ gridTemplateColumns: '2fr 0.8fr 0.5fr 0.8fr 0.7fr 0.7fr 0.7fr 0.6fr 0.7fr 0.6fr' }}
                        onClick={() => onAnalyze(r.ticker)}>
                        <div className="flex items-center gap-2">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-bold ${r.signal === "OVERSOLD" ? "bg-emerald-500/20 text-emerald-400" : r.signal === "OVERBOUGHT" ? "bg-red-500/20 text-red-400" : r.signal === "VOLUME SPIKE" ? "bg-amber-500/20 text-amber-400" : "bg-slate-700 text-slate-400"}`}>
                            {r.ticker.slice(0,2)}
                          </div>
                          <div><div className="font-semibold text-xs text-white">{r.ticker}</div></div>
                        </div>
                        <div className="text-right font-mono text-xs font-semibold text-white">${r.price}</div>
                        <div className="flex justify-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-[10px] ${r.score >= 50 ? "bg-cyan-500/20 text-cyan-400" : r.score >= 30 ? "bg-yellow-500/20 text-yellow-400" : "bg-slate-700 text-slate-400"}`}>{r.score}</div>
                        </div>
                        <div className="text-center">
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${r.signal === "OVERSOLD" ? "bg-emerald-500/15 text-emerald-400" : r.signal === "OVERBOUGHT" ? "bg-red-500/15 text-red-400" : r.signal === "VOLUME SPIKE" ? "bg-amber-500/15 text-amber-400" : r.signal === "BULLISH" ? "bg-cyan-500/15 text-cyan-400" : r.signal === "BEARISH" ? "bg-rose-500/15 text-rose-400" : "bg-slate-700 text-slate-400"}`}>{r.signal}</span>
                        </div>
                        <div className={`text-right font-mono text-xs ${r.change_1d >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.change_1d >= 0 ? "+" : ""}{r.change_1d}%</div>
                        <div className={`text-right font-mono text-xs ${r.change_1w >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.change_1w >= 0 ? "+" : ""}{r.change_1w}%</div>
                        <div className={`text-right font-mono text-xs ${r.change_1m >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.change_1m >= 0 ? "+" : ""}{r.change_1m}%</div>
                        <div className={`text-right font-mono text-xs ${r.rsi < 30 ? "text-emerald-400 font-bold" : r.rsi > 70 ? "text-red-400 font-bold" : "text-slate-300"}`}>{r.rsi}</div>
                        <div className={`text-right font-mono text-xs ${r.vol_ratio > 2 ? "text-amber-400 font-bold" : "text-slate-400"}`}>{r.vol_ratio}x</div>
                        <div className="text-center text-[8px] text-slate-500">{r.sector?.replace("_"," ")}</div>
                      </div>
                      <div className="sm:hidden px-3 py-2.5 border-b border-slate-700/30 cursor-pointer" onClick={() => onAnalyze(r.ticker)}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[9px] font-bold ${r.signal === "OVERSOLD" ? "bg-emerald-500/20 text-emerald-400" : r.signal === "OVERBOUGHT" ? "bg-red-500/20 text-red-400" : r.signal === "VOLUME SPIKE" ? "bg-amber-500/20 text-amber-400" : "bg-slate-700 text-slate-400"}`}>
                              {r.ticker.slice(0,2)}
                            </div>
                            <div>
                              <div className="font-bold text-sm text-white">{r.ticker}</div>
                              <div className="text-[9px] text-slate-500">{r.sector?.replace("_"," ")}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-mono text-sm font-bold text-white">${r.price}</div>
                            <div className={`font-mono text-xs font-bold ${r.change_1d >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.change_1d >= 0 ? "+" : ""}{r.change_1d}%</div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-1.5 gap-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${r.signal === "OVERSOLD" ? "bg-emerald-500/15 text-emerald-400" : r.signal === "OVERBOUGHT" ? "bg-red-500/15 text-red-400" : r.signal === "VOLUME SPIKE" ? "bg-amber-500/15 text-amber-400" : r.signal === "BULLISH" ? "bg-cyan-500/15 text-cyan-400" : r.signal === "BEARISH" ? "bg-rose-500/15 text-rose-400" : "bg-slate-700 text-slate-400"}`}>{r.signal}</span>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-[9px] ${r.score >= 50 ? "bg-cyan-500/20 text-cyan-400" : r.score >= 30 ? "bg-yellow-500/20 text-yellow-400" : "bg-slate-700 text-slate-400"}`}>{r.score}</div>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] font-mono">
                            <span className={r.change_1w >= 0 ? "text-emerald-400" : "text-red-400"}>1S {r.change_1w >= 0 ? "+" : ""}{r.change_1w}%</span>
                            <span className={`${r.rsi < 30 ? "text-emerald-400 font-bold" : r.rsi > 70 ? "text-red-400 font-bold" : "text-slate-400"}`}>RSI {r.rsi}</span>
                            {r.vol_ratio > 1.5 && <span className="text-amber-400 font-bold">{r.vol_ratio}x</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : massiveScan.loading ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <RefreshCw className="w-10 h-10 text-cyan-400 mx-auto mb-3 animate-spin" />
                <p className="text-slate-300 font-medium">Scan de 300+ actifs en cours...</p>
                <p className="text-xs text-slate-500 mt-1">US, EU, Crypto, ETFs, Commodités</p>
              </div>
            ) : massiveScan.results.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <Radar className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">Le scan se lance automatiquement.</p>
                <p className="text-xs text-slate-500 mt-1">Ou cliquez "Re-scanner" pour lancer manuellement</p>
              </div>
            ) : (
              <div className="glass-card rounded-xl p-8 text-center">
                <p className="text-slate-400">Aucun actif ne correspond aux filtres sélectionnés</p>
              </div>
            );
          })()}

          {/* AI Scan Analysis */}
          {massiveScan.results.length > 0 && (
            <div className="glass-card rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Bot className="w-4 h-4 text-cyan-400" /><span className="text-xs text-cyan-400 font-bold uppercase tracking-wider">Analyse IA du Scan</span></div>
              </div>
              {/* Quick analysis buttons */}
              <div className="flex gap-2 flex-wrap mb-3">
                {[
                  { label: "Analyse Complète du Scan", icon: "scan", prompt: `ANALYSE QUANTITATIVE DU SCAN (${massiveScan.results.length} actifs scannés).

Agis comme un analyste quantitatif. Pas de spéculation, uniquement les données.

CONTRAINTES: R/R minimum 2:1 | No Break No Trade | Score /100 obligatoire pour chaque opportunité (momentum 40pts + catalyseur 30pts + technique 30pts)

Pour chaque opportunité identifiée, fournir: Prix actuel, Zone d'entrée, Stop loss, Take profit, R/R, Score /100.` },
                  { label: "Top 5 Achats Immédiats", prompt: "Identifie les 5 meilleures opportunités d'achat immédiat avec score /100, zone d'entrée, stop loss et take profit. R/R minimum 2:1." },
                  { label: "Oversold + Volume Spike", prompt: "Actions oversold (RSI<30) avec volume spike: lesquelles sont de vrais retournements et lesquelles sont des pièges? Score /100 pour chaque." },
                  { label: "Breakouts Confirmés", prompt: "Liste tous les breakouts confirmés du scan. Pour chaque: prix, volume de confirmation, target, stop, R/R et score /100." },
                ].map((q, i) => (
                  <button key={i} onClick={() => {
                    const scanData = buildScanContext();
                    runScanAnalysis(`${q.prompt}\n\n${scanData}`, scanData);
                  }}
                    disabled={scanAnalysisLoading}
                    className={`px-3 py-2 ${i === 0 ? "bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 font-bold" : "bg-slate-800/50 border border-slate-700 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/30"} rounded-lg text-xs transition-all flex items-center gap-1.5 disabled:opacity-50`}
                    data-testid={`scan-analysis-${i}`}>
                    {i === 0 && <Zap className="w-3.5 h-3.5" />}
                    {scanAnalysisLoading && i === 0 ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Analyse...</> : q.label}
                  </button>
                ))}
              </div>

              {/* Inline analysis result */}
              <ScanAnalysisPanel
                result={scanAnalysis}
                loading={scanAnalysisLoading}
                onClose={() => setScanAnalysis(null)}
              />

              {/* Free input */}
              <div className="flex gap-2">
                <input value={scanChatInput || ""} onChange={(e) => setScanChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && scanChatInput && !scanAnalysisLoading) {
                      const scanData = buildScanContext();
                      runScanAnalysis(`${scanData}\n\nScoring obligatoire /100 (momentum 40pts + catalyseur 30pts + technique 30pts). R/R min 2:1.\n\nQUESTION: ${scanChatInput}`, scanData);
                      setScanChatInput("");
                    }
                  }}
                  placeholder="Pose ta question sur le scan (scoring /100 automatique)..."
                  className="flex-1 px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:outline-none"
                  data-testid="massive-chat-input" />
                <button onClick={() => {
                    if (!scanChatInput || scanAnalysisLoading) return;
                    const scanData = buildScanContext();
                    runScanAnalysis(`${scanData}\n\nScoring obligatoire /100 (momentum 40pts + catalyseur 30pts + technique 30pts). R/R min 2:1.\n\nQUESTION: ${scanChatInput}`, scanData);
                    setScanChatInput("");
                  }}
                  disabled={!scanChatInput || scanAnalysisLoading}
                  className="px-5 py-2.5 bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 font-bold text-sm rounded-lg flex items-center gap-2 disabled:opacity-40"
                  data-testid="massive-chat-send">
                  {scanAnalysisLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Consulter
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* SECTOR SCAN MODE (original) */}
      {scanMode === "sector" && (
        <>
          <div className="glass-card rounded-xl p-4 mb-6">
            <div className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-3">Select Sectors</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSelectedSectors(prev => prev.length === SECTOR_OPTIONS.length ? [] : SECTOR_OPTIONS.map(s => s.key))}
                className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${selectedSectors.length === SECTOR_OPTIONS.length ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" : "bg-slate-600 text-white border border-slate-500"}`}
                data-testid="scanner-select-all-btn">
                All
              </button>
              {SECTOR_OPTIONS.map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setSelectedSectors(prev => prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key])}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${selectedSectors.includes(key) ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/25" : "bg-slate-700 text-slate-300 border border-slate-600"}`}>
                  <Icon className="w-4 h-4" />{label}
                </button>
              ))}
            </div>
          </div>
          {scanning && (
            <div className="glass-card rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-300">Analyzing {scanProgress.sector}...</span>
                <span className="text-sm font-mono text-emerald-400">{scanProgress.current}/{scanProgress.total}</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-cyan-500 transition-all duration-300" style={{ width: `${(scanProgress.current / scanProgress.total) * 100}%` }} />
              </div>
            </div>
          )}
          {scanResults.length > 0 ? (
            <>
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-700 flex justify-between items-center">
                <h2 className="font-display text-lg font-bold">{scanResults.length} Opportunities Ranked</h2>
                <button onClick={() => {
                    const top5 = scanResults.slice(0, 10).map(r => `${r.ticker} (${r.name}): $${r.price}, Score ${r.score}, Signal ${r.signal}, RSI ${r.rsi}, Support $${r.support}, Résistance $${r.resistance}, Entrée $${r.entry_price}, Trend ${r.trend}`).join("\n");
                    const scanData = `RÉSULTATS DU SCAN — TOP 10:\n${top5}`;
                    runScanAnalysis(`Based on scan results, give me TOP 3 trades with entry, stop-loss, target. Score /100 obligatoire.\n\n${scanData}`, scanData);
                  }}
                  disabled={scanAnalysisLoading}
                  className="px-4 py-2 bg-cyan-500/15 text-cyan-400 border border-cyan-500/25 rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
                  data-testid="sector-ai-analysis-btn">
                  {scanAnalysisLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}AI Analysis
                </button>
              </div>
              <div className="grid px-5 py-3 bg-slate-800/50 border-b border-slate-700 table-header" style={{ gridTemplateColumns: '2fr 0.8fr 0.6fr 0.7fr 0.6fr 0.7fr 0.8fr 0.8fr 0.7fr 0.7fr' }}>
                <div>Asset</div><div className="text-right">Price</div><div className="text-center">Score</div>
                <div className="text-center">Signal</div><div className="text-right">RSI</div><div className="text-right">Support</div>
                <div className="text-right">Resistance</div><div className="text-right text-cyan-400">Entrée</div><div className="text-center">Trend</div><div className="text-center">Chart</div>
              </div>
              {scanResults.map((r, i) => (
                <div key={i} className={`grid px-5 py-3.5 items-center row-hover ${i < scanResults.length - 1 ? "border-b border-slate-700/50" : ""}`}
                  style={{ gridTemplateColumns: '2fr 0.8fr 0.6fr 0.7fr 0.6fr 0.7fr 0.8fr 0.8fr 0.7fr 0.7fr' }}
                  onClick={() => onAnalyze(r.ticker)}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center text-sm">{r.flag || <SectorIcon sector={r.sector} />}</div>
                    <div><div className="font-semibold text-sm text-white">{r.ticker}</div><div className="text-xs text-slate-400">{r.name}</div></div>
                  </div>
                  <div className="text-right font-mono text-sm font-semibold text-white">{formatTickerPrice(r.price, r.ticker)}</div>
                  <div className="flex justify-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${r.score >= 70 ? "bg-cyan-500/20 text-cyan-400" : r.score >= 40 ? "bg-yellow-500/20 text-yellow-400" : "bg-red-500/20 text-red-400"}`}>{r.score}</div>
                  </div>
                  <div className="text-center"><SignalBadge signal={r.signal} /></div>
                  <div className={`text-right font-mono text-sm ${r.rsi < 30 ? "text-emerald-400" : r.rsi > 70 ? "text-red-400" : "text-slate-300"}`}>{r.rsi || "—"}</div>
                  <div className="text-right font-mono text-xs text-emerald-400">{r.support ? `${getTickerCurrency(r.ticker)}${r.support}` : "—"}</div>
                  <div className="text-right font-mono text-xs text-red-400">{r.resistance ? `${getTickerCurrency(r.ticker)}${r.resistance}` : "—"}</div>
                  <div className="text-right font-mono text-xs font-bold text-cyan-400">{r.entry_price ? `${getTickerCurrency(r.ticker)}${r.entry_price}` : "—"}</div>
                  <div className="text-center text-xs text-slate-400">{r.trend?.replace("_", " ") || "—"}</div>
                  <div className="flex justify-center"><Sparkline values={r.sparkline} width={70} height={24} /></div>
                </div>
              ))}
            </div>

            {/* Inline analysis result for sector mode */}
            <ScanAnalysisPanel
              result={scanAnalysis}
              loading={scanAnalysisLoading}
              onClose={() => setScanAnalysis(null)}
            />

            {/* Scanner AI Chat */}
            <div className="glass-card rounded-xl p-4 mt-4">
              <div className="flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-cyan-400" /><span className="text-xs text-cyan-400 font-bold uppercase tracking-wider">Demander au Comité d'Experts</span></div>
              <div className="flex gap-2">
                <input value={scanChatInput || ""} onChange={(e) => setScanChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && scanChatInput && !scanAnalysisLoading) {
                      const top5 = scanResults.slice(0, 5).map(r => `${r.ticker} (${r.name}): Prix $${r.price}, Score ${r.score}, Signal ${r.signal}, RSI ${r.rsi}, Support $${r.support}, Resistance $${r.resistance}, Entrée $${r.entry_price}, Trend ${r.trend}`).join("\n");
                      const scanData = `RÉSULTATS DU SCAN — TOP 5 OPPORTUNITÉS:\n${top5}`;
                      runScanAnalysis(`${scanData}\n\nMA QUESTION: ${scanChatInput}`, scanData);
                      setScanChatInput("");
                    }
                  }}
                  placeholder="Ex: Lequel acheter en premier ? Quel sizing pour 5000$ ? Est-ce le bon moment ?"
                  className="flex-1 px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-600 focus:border-cyan-500/50 focus:outline-none"
                  data-testid="scanner-chat-input" />
                <button onClick={() => {
                    if (!scanChatInput || scanAnalysisLoading) return;
                    const top5 = scanResults.slice(0, 5).map(r => `${r.ticker} (${r.name}): Prix $${r.price}, Score ${r.score}, Signal ${r.signal}, RSI ${r.rsi}, Support $${r.support}, Resistance $${r.resistance}, Entrée $${r.entry_price}, Trend ${r.trend}`).join("\n");
                    const scanData = `RÉSULTATS DU SCAN — TOP 5 OPPORTUNITÉS:\n${top5}`;
                    runScanAnalysis(`${scanData}\n\nMA QUESTION: ${scanChatInput}`, scanData);
                    setScanChatInput("");
                  }}
                  disabled={!scanChatInput || scanAnalysisLoading}
                  className="px-5 py-2.5 bg-gradient-to-r from-cyan-300 to-blue-500 text-slate-900 font-bold text-sm rounded-lg flex items-center gap-2 disabled:opacity-40"
                  data-testid="scanner-chat-send">
                  {scanAnalysisLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Consulter
                </button>
              </div>
              <div className="flex gap-2 mt-2 flex-wrap">
                {["Quel est le meilleur trade du scan ?", "Zones d'entrée et stop-loss pour le top 3", "Lequel acheter en priorité avec 5000$ ?"].map((q, i) => (
                  <button key={i} onClick={() => {
                    const top5 = scanResults.slice(0, 5).map(r => `${r.ticker}: $${r.price}, Score ${r.score}, ${r.signal}, RSI ${r.rsi}, Entrée $${r.entry_price}`).join("\n");
                    const scanData = `SCAN TOP 5:\n${top5}`;
                    runScanAnalysis(`${scanData}\n\n${q}`, scanData);
                  }}
                    disabled={scanAnalysisLoading}
                    className="px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-[10px] text-slate-400 hover:text-cyan-400 hover:border-cyan-500/30 transition-all disabled:opacity-50"
                    data-testid={`scanner-quick-q-${i}`}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
            </>
          ) : !scanning && (
            <div className="glass-card rounded-xl p-16 text-center">
              <Radar className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">Select sectors and run scan to find opportunities</p>
            </div>
          )}
        </>
      )}
    </div>
  );
})
